import React, { createContext, useContext, useEffect, useState } from 'react';
import API_BASE_URL from '../config/api';

const AuthContext = createContext(null);

const LS_USERS_KEY = 'matikamistr_users';
const LS_CURRENT_KEY = 'matikamistr_current';

function readUsers() {
  try { return JSON.parse(localStorage.getItem(LS_USERS_KEY) || '[]'); } catch(e){ return []; }
}

function writeUsers(users) { localStorage.setItem(LS_USERS_KEY, JSON.stringify(users)); }

export function AuthProvider({ children }){
  const [users, setUsers] = useState(() => readUsers());
  const [current, setCurrent] = useState(() => {
    try { 
      // prefer server JWT token stored in localStorage under matikamistr_token
      const token = localStorage.getItem('matikamistr_token');
      if (token) {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
          return { 
            id: payload.id, 
            name: payload.name || payload.email, 
            email: payload.email, 
            role: payload.role,
            grade: payload.grade || 'grade_5',
            classroom_code: payload.classroom_code || null
          };
        }
      }
      return JSON.parse(localStorage.getItem(LS_CURRENT_KEY));
    } catch(e){ return null; }
  });

  const [simulateGuestMode, setSimulateGuestMode] = useState(() => {
    try {
      return localStorage.getItem('simulate_guest_mode') === 'true';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('simulate_guest_mode', simulateGuestMode ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [simulateGuestMode]);

  useEffect(() => { writeUsers(users); }, [users]);
  useEffect(() => { localStorage.setItem(LS_CURRENT_KEY, JSON.stringify(current)); }, [current]);

  // React to token changes outside of AuthContext (e.g. DemoLogin direct localStorage set)
  useEffect(() => {
    function refreshFromToken() {
      try {
        const token = localStorage.getItem('matikamistr_token');
        if (!token) return; // keep existing state (could be logout elsewhere)
        const parts = token.split('.');
        if (parts.length < 2) return;
        const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
        // Only update if changed (avoid unnecessary re-renders)
        setCurrent(prev => {
          if (prev && prev.email === payload.email && prev.role === payload.role) return prev;
          return {
            id: payload.id,
            name: payload.name || payload.email,
            email: payload.email,
            role: payload.role,
            grade: payload.grade || 'grade_5',
            classroom_code: payload.classroom_code || null
          };
        });
      } catch (e) { /* ignore malformed token */ }
    }
    refreshFromToken(); // initial mount
    window.addEventListener('auth:token-updated', refreshFromToken);
    window.addEventListener('storage', (e) => { if (e.key === 'matikamistr_token') refreshFromToken(); });
    return () => {
      window.removeEventListener('auth:token-updated', refreshFromToken);
    };
  }, []);

  // Registrace přes server
  const register = async ({ name, email, password, grade }) => {
    if (!email || !password) throw new Error('E-mail a heslo jsou povinné');
    const resp = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, grade: grade || 'grade_5' })
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || 'Chyba registrace');
    }
    const data = await resp.json();
    if (data.token) {
      localStorage.setItem('matikamistr_token', data.token);
      const parts = data.token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
      setCurrent({ 
        id: payload.id, 
        name: payload.name || payload.email, 
        email: payload.email, 
        role: payload.role,
        grade: payload.grade || 'grade_5',
        classroom_code: payload.classroom_code || null
      });
    }
    return data;
  };

  // Login přes server
  const login = async ({ email, password }) => {
    const resp = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || 'Chyba přihlášení');
    }
    const data = await resp.json();
    if (data.token) {
      localStorage.setItem('matikamistr_token', data.token);
      const parts = data.token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
      setCurrent({ 
        id: payload.id, 
        name: payload.name || payload.email, 
        email: payload.email, 
        role: payload.role,
        grade: payload.grade || 'grade_5',
        classroom_code: payload.classroom_code || null
      });
    }
    return data;
  };

  // NOTE: this decodes the ID token client-side to extract profile info.
  // It DOES NOT verify the token signature. For production, verify token on the server.
  // loginWithGoogle: prefer server-provided JWT (matikamistr_token), or accept idToken fallback
  const loginWithGoogle = ({ idToken, serverToken }) => {
    // serverToken: server's own JWT (recommended)
    if (serverToken) {
      try {
        localStorage.setItem('matikamistr_token', serverToken);
        const parts = serverToken.split('.');
        const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
        const user = { 
          id: payload.id, 
          name: payload.name || payload.email, 
          email: payload.email, 
          role: payload.role,
          grade: payload.grade || 'grade_5',
          classroom_code: payload.classroom_code || null
        };
        setCurrent(user);
        return user;
      } catch (e) { throw new Error('Invalid server token'); }
    }

    // fallback: decode Google idToken locally (no verification)
    if (!idToken) throw new Error('idToken missing');
    const parts = idToken.split('.');
    if (parts.length < 2) throw new Error('Invalid idToken');
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
      const user = { 
        id: payload.sub, 
        name: payload.name || payload.email, 
        email: payload.email, 
        picture: payload.picture,
        grade: 'grade_5'
      };
      setUsers(prev => {
        if (prev.find(u => u.email === user.email)) return prev;
        return [...prev, { ...user, password: null }];
      });
      setCurrent({ id: user.id, name: user.name, email: user.email, picture: user.picture });
      return user;
    } catch (e) { throw new Error('Neplatný idToken payload'); }
  };

  const logout = () => { setCurrent(null); };

  return (
    <AuthContext.Provider value={{ users, current, register, login, loginWithGoogle, logout, simulateGuestMode, setSimulateGuestMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(){
  return useContext(AuthContext);
}
